import React, { useEffect, useRef, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { cn } from "lib/utils";

// Native type=time selects the whole clock and types AM/PM; one keystroke here is one digit.

type ClockDigits = [string, string, string, string];
type Meridian = "AM" | "PM";

const EMPTY_DIGITS: ClockDigits = ["", "", "", ""];

function emptyClockDigits(): ClockDigits {
  return [...EMPTY_DIGITS] as ClockDigits;
}

function parseHHmm(
  value: string
): { digits: ClockDigits; meridian: Meridian } | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    return null;
  }
  const meridian: Meridian = hours < 12 ? "AM" : "PM";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  const hour = String(hour12).padStart(2, "0");
  const minute = String(minutes).padStart(2, "0");
  return {
    digits: [hour[0], hour[1], minute[0], minute[1]],
    meridian,
  };
}

function formatHHmm(digits: ClockDigits, meridian: Meridian): string | null {
  if (digits.some((digit) => digit === "")) {
    return null;
  }
  const hour12 = Number(digits[0] + digits[1]);
  const minutes = Number(digits[2] + digits[3]);
  if (hour12 < 1 || hour12 > 12 || minutes > 59) {
    return null;
  }
  const hour24 =
    meridian === "AM"
      ? hour12 === 12
        ? 0
        : hour12
      : hour12 === 12
        ? 12
        : hour12 + 12;
  return `${String(hour24).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function clampHourOnes(tens: string, ones: string): string {
  if (ones === "") {
    return ones;
  }
  if (tens === "1") {
    return ones === "0" || ones === "1" || ones === "2" ? ones : "0";
  }
  if (tens === "0") {
    return ones === "0" ? "1" : ones;
  }
  return ones;
}

interface TimeFieldProps {
  value: string;
  onChange: (value: string) => void;
  "aria-label": string;
  "data-testid": string;
}

const PLACE_LABELS = ["hour tens", "hour ones", "minute tens", "minute ones"] as const;

function TimeField({
  value,
  onChange,
  "aria-label": ariaLabel,
  "data-testid": testId,
}: TimeFieldProps) {
  const parsed = parseHHmm(value);
  const [digits, setDigits] = useState<ClockDigits>(
    () => parsed?.digits ?? emptyClockDigits()
  );
  const [meridian, setMeridian] = useState<Meridian>(parsed?.meridian ?? "AM");
  const digitRefs = useRef<Array<HTMLInputElement | null>>([null, null, null, null]);

  useEffect(() => {
    const next = parseHHmm(value);
    if (next) {
      setDigits(next.digits);
      setMeridian(next.meridian);
    } else if (value === "") {
      setDigits(emptyClockDigits());
    }
  }, [value]);

  const emit = (nextDigits: ClockDigits, nextMeridian: Meridian) => {
    setDigits(nextDigits);
    setMeridian(nextMeridian);
    const hhmm = formatHHmm(nextDigits, nextMeridian);
    if (hhmm !== null) {
      onChange(hhmm);
    }
  };

  const focusPlace = (index: number) => {
    const clamped = Math.max(0, Math.min(3, index));
    const node = digitRefs.current[clamped];
    node?.focus();
    node?.select();
  };

  const applyDigit = (index: number, typed: string) => {
    if (index === 0 && typed >= "2" && typed <= "9") {
      emit(["0", typed, digits[2], digits[3]], meridian);
      focusPlace(2);
      return;
    }
    if (index === 0 && typed !== "0" && typed !== "1") {
      return;
    }
    if (index === 1) {
      const tens = digits[0] === "" ? "0" : digits[0];
      typed = clampHourOnes(tens, typed);
      if (tens === "1" && typed !== "0" && typed !== "1" && typed !== "2") {
        return;
      }
      if (tens === "0" && typed === "0") {
        return;
      }
    }
    if (index === 2 && typed > "5") {
      return;
    }
    const next: ClockDigits = [...digits] as ClockDigits;
    if (index === 0) {
      next[0] = typed;
      next[1] = clampHourOnes(typed, digits[1]);
    } else {
      if (index === 1 && digits[0] === "") {
        next[0] = "0";
      }
      next[index] = typed;
    }
    emit(next, meridian);
    if (index < 3) {
      focusPlace(index + 1);
    }
  };

  const onDigitKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key >= "0" && event.key <= "9") {
      event.preventDefault();
      applyDigit(index, event.key);
      return;
    }
    if (event.key === "Backspace") {
      event.preventDefault();
      if (digits[index] !== "") {
        const next: ClockDigits = [...digits] as ClockDigits;
        next[index] = "";
        setDigits(next);
      } else {
        focusPlace(index - 1);
      }
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      focusPlace(index - 1);
      return;
    }
    if (event.key === "ArrowRight" || event.key === ":") {
      event.preventDefault();
      focusPlace(index + 1);
    }
  };

  const onGroupBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node)) {
      return;
    }
    const valid = parseHHmm(value);
    if (valid) {
      setDigits(valid.digits);
      setMeridian(valid.meridian);
    }
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex items-center rounded-lg border border-brand-100 bg-white py-0.5 pl-1.5 pr-0.5"
      onBlur={onGroupBlur}
    >
      <input
        data-testid={testId}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {([0, 1] as const).map((index) => (
        <DigitInput
          key={index}
          inputRef={(node) => {
            digitRefs.current[index] = node;
          }}
          value={digits[index]}
          aria-label={`${ariaLabel} ${PLACE_LABELS[index]}`}
          onKeyDown={(event) => onDigitKeyDown(index, event)}
          onTyped={(typed) => applyDigit(index, typed)}
        />
      ))}
      <span className="w-2.5 text-center text-xs font-semibold text-brand/50" aria-hidden>
        :
      </span>
      {([2, 3] as const).map((index) => (
        <DigitInput
          key={index}
          inputRef={(node) => {
            digitRefs.current[index] = node;
          }}
          value={digits[index]}
          aria-label={`${ariaLabel} ${PLACE_LABELS[index]}`}
          onKeyDown={(event) => onDigitKeyDown(index, event)}
          onTyped={(typed) => applyDigit(index, typed)}
        />
      ))}
      <Select
        value={meridian}
        onValueChange={(next) => emit(digits, next as Meridian)}
      >
        <SelectTrigger
          size="sm"
          aria-label={`${ariaLabel} AM or PM`}
          data-testid={`${testId}-meridian`}
          className="ml-1 h-7 w-[3.35rem] gap-0.5 border-0 bg-brand-50 px-1.5 text-[11px] font-semibold text-brand shadow-none hover:bg-brand-100 focus-visible:ring-brand/30"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="z-[70] min-w-[4.5rem] bg-white">
          <SelectItem value="AM" className="text-xs">
            AM
          </SelectItem>
          <SelectItem value="PM" className="text-xs">
            PM
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function DigitInput({
  value,
  inputRef,
  onKeyDown,
  onTyped,
  "aria-label": ariaLabel,
}: {
  value: string;
  inputRef: (node: HTMLInputElement | null) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onTyped: (digit: string) => void;
  "aria-label": string;
}) {
  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      maxLength={1}
      aria-label={ariaLabel}
      value={value}
      placeholder="–"
      onChange={(event) => {
        const digit = event.target.value.replace(/\D/g, "").slice(-1);
        if (digit) {
          onTyped(digit);
        }
      }}
      onKeyDown={onKeyDown}
      onFocus={(event) => event.currentTarget.select()}
      onMouseUp={(event) => event.preventDefault()}
      className={cn(
        "h-7 w-5 rounded-md bg-transparent text-center text-sm font-semibold tabular-nums text-brand",
        "caret-transparent placeholder:text-brand/30",
        "selection:bg-brand selection:text-white",
        "focus:bg-brand focus:text-white focus:placeholder:text-white/50",
        "outline-none"
      )}
    />
  );
}

export default TimeField;
